import { useCallback, useEffect, useRef, useState } from 'react';

const IOS_CHECK_TIMEOUT_MS = 3000;

// Single-consumer global bridge handler (OfflinePunchProvider only).
let iosPersistentHandler = null;
let activeIosPullGeneration = 0;
let iosInFlightPromise = null;
let activePullTimeoutId = null;
let activePullCancel = null;

const hasAndroidBridge = () =>
  typeof window?.AndroidNative?.isDeviceOnline === 'function';

const hasIosBridge = () =>
  typeof window?.webkit?.messageHandlers?.checkNetworkStatus?.postMessage ===
  'function';

const readAndroidOnline = () => window.AndroidNative.isDeviceOnline();

const readBrowserOnline = () =>
  typeof navigator !== 'undefined' ? navigator.onLine : true;

const resolveNativePlatform = () => {
  if (hasIosBridge()) {
    return 'native-ios';
  }

  if (hasAndroidBridge()) {
    return 'native-android';
  }

  return 'browser';
};

// Native may call receiveiOSNetworkStatus before React mounts the hook, so a
// callable handler must always be present on window.
const receiveIosNetworkStatusFallback = (isOnline) => {
  if (typeof iosPersistentHandler === 'function') {
    iosPersistentHandler(isOnline);
  }
};

if (typeof window !== 'undefined' && !window.receiveiOSNetworkStatus) {
  window.receiveiOSNetworkStatus = receiveIosNetworkStatusFallback;
}

const restoreIosPersistentHandler = (temporaryCallback) => {
  if (window.receiveiOSNetworkStatus !== temporaryCallback) {
    return;
  }

  if (typeof iosPersistentHandler === 'function') {
    window.receiveiOSNetworkStatus = iosPersistentHandler;
    return;
  }

  window.receiveiOSNetworkStatus = receiveIosNetworkStatusFallback;
};

const clearActivePullTracking = () => {
  activePullTimeoutId = null;
  activePullCancel = null;
};

const cancelActiveIosPull = () => {
  activePullCancel?.();
  iosInFlightPromise = null;
};

const performIosPull = (timeoutMs = IOS_CHECK_TIMEOUT_MS) => {
  if (!hasIosBridge()) {
    return Promise.resolve(readBrowserOnline());
  }

  const pullGeneration = ++activeIosPullGeneration;

  return new Promise((resolve) => {
    let settled = false;

    const finish = (isOnline) => {
      if (settled || pullGeneration !== activeIosPullGeneration) {
        return;
      }
      settled = true;
      clearActivePullTracking();
      resolve(Boolean(isOnline));
    };

    const temporaryCallback = (isOnline) => {
      if (activePullTimeoutId !== null) {
        clearTimeout(activePullTimeoutId);
        activePullTimeoutId = null;
      }
      restoreIosPersistentHandler(temporaryCallback);
      finish(isOnline);
    };

    const timeoutId = setTimeout(() => {
      activePullTimeoutId = null;
      restoreIosPersistentHandler(temporaryCallback);
      finish(readBrowserOnline());
    }, timeoutMs);

    activePullTimeoutId = timeoutId;
    activePullCancel = () => {
      if (settled) {
        return;
      }
      settled = true;
      ++activeIosPullGeneration;
      if (activePullTimeoutId !== null) {
        clearTimeout(activePullTimeoutId);
        activePullTimeoutId = null;
      }
      restoreIosPersistentHandler(temporaryCallback);
      clearActivePullTracking();
      resolve(readBrowserOnline());
    };

    window.receiveiOSNetworkStatus = temporaryCallback;

    window.webkit.messageHandlers.checkNetworkStatus.postMessage(null);
  });
};

const checkIosOnline = (timeoutMs = IOS_CHECK_TIMEOUT_MS) => {
  if (iosInFlightPromise) {
    return iosInFlightPromise;
  }

  const trackedPromise = performIosPull(timeoutMs).finally(() => {
    if (iosInFlightPromise === trackedPromise) {
      iosInFlightPromise = null;
    }
  });
  iosInFlightPromise = trackedPromise;

  return trackedPromise;
};

const readInitialOnlineState = async () => {
  const source = resolveNativePlatform();

  if (source === 'native-android') {
    return { isOnline: readAndroidOnline(), source };
  }

  if (source === 'native-ios') {
    const isOnline = await checkIosOnline();
    return { isOnline, source };
  }

  return { isOnline: readBrowserOnline(), source };
};

/** @internal Resets module-level iOS pull state between unit tests. */
export const resetIosNetworkPullStateForTests = () => {
  cancelActiveIosPull();
  iosInFlightPromise = null;
  activeIosPullGeneration = 0;
  iosPersistentHandler = null;
};

/**
 * Connectivity hook for mobile-mode WebClock.
 * Prefers native bridge signals; falls back to browser online/offline APIs.
 *
 * @param {Object} [options]
 * @param {() => void} [options.onConnectivityChange] - Called whenever online state is set (including initial check).
 * @param {() => void} [options.onUnmount] - Called on hook teardown (e.g. clear scheduled work).
 */
export function useNetworkStatus({ onConnectivityChange, onUnmount } = {}) {
  const [isOnline, setIsOnline] = useState(true);
  const [source, setSource] = useState('unknown');
  const mountedRef = useRef(true);
  const nativePlatformRef = useRef(null);
  const refreshGenerationRef = useRef(0);
  const onConnectivityChangeRef = useRef(onConnectivityChange);
  const onUnmountRef = useRef(onUnmount);

  onConnectivityChangeRef.current = onConnectivityChange;
  onUnmountRef.current = onUnmount;

  const applyOnlineState = useCallback((online, nextSource) => {
    if (!mountedRef.current) {
      return;
    }
    setIsOnline(online);
    if (nextSource) {
      setSource(nextSource);
    }
    onConnectivityChangeRef.current?.(online);
  }, []);

  const refreshOnlineState = useCallback(async () => {
    const refreshGeneration = ++refreshGenerationRef.current;
    nativePlatformRef.current = resolveNativePlatform();
    const next = await readInitialOnlineState();

    if (
      refreshGeneration !== refreshGenerationRef.current ||
      !mountedRef.current
    ) {
      return;
    }

    nativePlatformRef.current = next.source;
    applyOnlineState(next.isOnline, next.source);
  }, [applyOnlineState]);

  useEffect(() => {
    mountedRef.current = true;

    let previousIosCallback;
    const handleIosNetworkStatus = (online) => {
      if (!mountedRef.current || !hasIosBridge()) {
        return;
      }
      nativePlatformRef.current = 'native-ios';
      applyOnlineState(Boolean(online), 'native-ios');
    };

    if (hasIosBridge()) {
      previousIosCallback = window.receiveiOSNetworkStatus;
      iosPersistentHandler = handleIosNetworkStatus;
      window.receiveiOSNetworkStatus = handleIosNetworkStatus;
    }

    refreshOnlineState();

    const handleNativeOnline = () =>
      applyOnlineState(true, nativePlatformRef.current);
    const handleNativeOffline = () =>
      applyOnlineState(false, nativePlatformRef.current);
    const handleBrowserOnline = () => applyOnlineState(true, 'browser');
    const handleBrowserOffline = () => applyOnlineState(false, 'browser');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshOnlineState();
      }
    };

    window.addEventListener('nativeOnline', handleNativeOnline);
    window.addEventListener('nativeOffline', handleNativeOffline);
    window.addEventListener('online', handleBrowserOnline);
    window.addEventListener('offline', handleBrowserOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      onUnmountRef.current?.();

      cancelActiveIosPull();

      if (iosPersistentHandler === handleIosNetworkStatus) {
        iosPersistentHandler = null;
      }

      if (window.receiveiOSNetworkStatus === handleIosNetworkStatus) {
        window.receiveiOSNetworkStatus =
          typeof previousIosCallback === 'function'
            ? previousIosCallback
            : receiveIosNetworkStatusFallback;
      }

      window.removeEventListener('nativeOnline', handleNativeOnline);
      window.removeEventListener('nativeOffline', handleNativeOffline);
      window.removeEventListener('online', handleBrowserOnline);
      window.removeEventListener('offline', handleBrowserOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [applyOnlineState, refreshOnlineState]);

  return { isOnline, source };
}
